/**
 * The best-time ghost: the player's fastest saved run on the course, run again by a second
 * simulation from its tape and drawn as a ghost in the player's viewport, so the player races
 * their own record. The two runs are aligned at the start line: when the player's run starts, the
 * ghost's simulation is wound forward to its own start line crossing, and from there both advance
 * one tick per tick. When the tape runs out the ghost holds its last state (frozen at the finish).
 */

import { type RenderFeel, ViewState } from "@lache/render";
import { type CourseData, createInputFrame, Sim, type Tape, TapeReader, type Tuning } from "@lache/sim";
import { replayTuning } from "./replay-tuning";

export class BestGhost {
  sim: Sim;
  readonly view = new ViewState();
  private readonly input = createInputFrame();
  private reader: TapeReader | null = null;
  private tape: Tape | null = null;
  private startTick = 0;
  /** The time the ghost's run took, s, for the HUD. */
  time: number | null = null;
  /** Running beside the player (from the player's start line crossing to the player's restart). */
  armed = false;

  constructor(
    course: CourseData,
    private tuning: Tuning,
  ) {
    this.sim = new Sim(course, tuning);
  }

  /** Whether a run is loaded to race against. */
  get loaded(): boolean {
    return this.tape !== null;
  }

  setCourse(course: CourseData, tuning: Tuning): void {
    this.tuning = tuning;
    this.sim = new Sim(course, tuning);
    this.disarm();
  }

  setTuning(tuning: Tuning): void {
    this.tuning = tuning;
    const recorded = this.tape ? replayTuning(this.tape.tuningHash, tuning) : null;
    this.sim.setTuning(recorded ?? tuning);
    if (this.tape && !recorded) this.load(null, 0, null);
  }

  /** The run to race: its tape from the course's start, the tick its clock started, its time. */
  load(tape: Tape | null, startTick: number, time: number | null): void {
    const recorded = tape ? replayTuning(tape.tuningHash, this.tuning) : null;
    this.sim.setTuning(recorded ?? this.tuning);
    this.tape = recorded ? tape : null;
    this.startTick = startTick;
    this.time = this.tape ? time : null;
    this.disarm();
  }

  /**
   * The player just crossed the start line: put the ghost one tick short of its own crossing, so the
   * tick that follows in the same step brings it level with the player. Winding forward takes a
   * few milliseconds at most (a tick is microseconds), once per run. With `startTick` 0 (a replay's
   * ghost, wound together with the cams) nothing is skipped.
   */
  arm(): void {
    if (!this.tape) return;
    this.sim.reset(0);
    this.reader = new TapeReader(this.tape);
    for (let i = 0; i < this.startTick - 1; i++) {
      if (!this.reader.next(this.input)) break;
      this.sim.step(this.input);
      this.sim.events.clear();
    }
    this.sim.prev.copyFrom(this.sim.state);
    this.armed = true;
  }

  disarm(): void {
    this.armed = false;
    this.reader = null;
  }

  /** One tick beside the player's; past the end of the tape the ghost stands where it finished. */
  tick(): void {
    if (!this.armed || !this.reader) return;
    if (this.reader.next(this.input)) {
      this.sim.step(this.input);
      this.sim.events.clear();
    } else {
      this.reader = null;
      this.sim.prev.copyFrom(this.sim.state);
    }
  }

  updateView(alpha: number, feel: RenderFeel, dt: number): void {
    this.view.update(this.sim.prev, this.sim.state, alpha, this.sim.tuning, feel, dt);
  }
}
